import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import type { CatalogCleanParams, Generation, ProductShootParams } from '../types/generation';

interface CreateShootResponse {
  id: string;
  status: string;
  createdAt: string;
}

export function useCreateProductShoot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductShootParams) =>
      api<CreateShootResponse>('/generations/product-shoot', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) => {
      // Optimistic: insert a fake pending generation at the top of the list
      // so the card appears instantly without waiting for server
      const optimisticId = `optimistic-${Date.now()}`;
      const optimistic: Generation = {
        id: optimisticId,
        type: 'product_shoot',
        status: 'pending',
        productId: payload.productId,
        attempts: 0,
        createdAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
        failedAt: null,
        errorMessage: null,
        generatedImages: [],
        metadata: {
          intent: payload.intent ?? 'studio_model',
          fashnModel: 'product-to-model',
          aspectRatio: payload.intent === 'lifestyle_shoot' ? '4:5' : '3:4',
          providerRoute: {
            provider: 'fashn',
            capability: 'product_to_model',
            model: 'product-to-model',
          },
          modelType: payload.modelType,
          background: payload.background,
          numSamples: payload.numSamples,
        },
      };
      qc.setQueryData<{ pages: { items: Generation[]; nextCursor: string | null }[] }>(
        ['generations', {}],
        (old) => {
          if (!old) return old;
          const pages = old.pages.slice();
          pages[0] = { ...pages[0], items: [optimistic, ...pages[0].items] };
          return { ...old, pages };
        },
      );
      return { optimisticId };
    },
    onSuccess: async (_data, _vars, _ctx) => {
      haptic.impact('light');
      // Invalidate so the real generation replaces the optimistic one
      await qc.invalidateQueries({ queryKey: ['generations'] });
    },
    onError: (_err, _vars, ctx) => {
      haptic.error();
      // Remove optimistic entry on error
      if (ctx?.optimisticId) {
        qc.setQueryData<{ pages: { items: Generation[]; nextCursor: string | null }[] }>(
          ['generations', {}],
          (old) => {
            if (!old) return old;
            const pages = old.pages.map((p) => ({
              ...p,
              items: p.items.filter((g) => g.id !== ctx.optimisticId),
            }));
            return { ...old, pages };
          },
        );
      }
    },
  });
}

export function useCreateCatalogClean() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CatalogCleanParams) =>
      api<CreateShootResponse>('/generations/catalog-clean', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) => {
      const optimisticId = `optimistic-catalog-${Date.now()}`;
      const optimistic: Generation = {
        id: optimisticId,
        type: 'catalog_clean',
        status: 'pending',
        productId: payload.productId,
        attempts: 0,
        createdAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
        failedAt: null,
        errorMessage: null,
        generatedImages: [],
        metadata: {
          intent: 'catalog_clean',
          aspectRatio: '1:1',
          providerRoute: {
            provider: 'gemini',
            capability: 'image_edit',
            model: 'gemini-3.1-flash-image-preview',
          },
        },
      };
      qc.setQueryData<{ pages: { items: Generation[]; nextCursor: string | null }[] }>(
        ['generations', {}],
        (old) => {
          if (!old) return old;
          const pages = old.pages.slice();
          pages[0] = { ...pages[0], items: [optimistic, ...pages[0].items] };
          return { ...old, pages };
        },
      );
      return { optimisticId };
    },
    onSuccess: async () => {
      haptic.impact('light');
      await qc.invalidateQueries({ queryKey: ['generations'] });
    },
    onError: (_err, _vars, ctx) => {
      haptic.error();
      if (ctx?.optimisticId) {
        qc.setQueryData<{ pages: { items: Generation[]; nextCursor: string | null }[] }>(
          ['generations', {}],
          (old) => {
            if (!old) return old;
            const pages = old.pages.map((p) => ({
              ...p,
              items: p.items.filter((g) => g.id !== ctx.optimisticId),
            }));
            return { ...old, pages };
          },
        );
      }
    },
  });
}
